import React, { useContext, useEffect, useMemo } from 'react';
import {
  Badge,
  Box,
  Button,
  Container,
  Heading,
  HStack,
  List,
  ListIcon,
  ListItem,
  SimpleGrid,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react';
import { CheckIcon } from '@chakra-ui/icons';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Navbar from '../compnents/Navbar';
import Footer from '../compnents/Footer';
import AuthContext from '../context/AuthContext';
import { DATA_NEEDED, OFFER, PLAN_ORDER, formatEuro } from '../config/plans';
import { PlanCard, PlanComparisonTable } from '../compnents/PlanCatalog';
import { buildPurchaseIntentUrl, normalizePurchaseIntent } from '../utils/purchaseIntent';
import useDocumentMeta from '../hooks/useDocumentMeta';

const capabilityColumns = [
  {
    title: 'Commercial intelligence',
    items: [
      'Behavioral bias detection and investor insight',
      'Prioritized next actions for relationship managers',
      'Customer segmentation and risk-oriented views',
      'Reporting and export flows for stakeholder sharing',
    ],
  },
  {
    title: 'Role-based experiences',
    items: [
      'Admin workspace for setup and control',
      'RM dashboards including who-to-call and own-book views',
      'Business-only Compliance and audit oversight',
      'Business-only Head of Advisory, Portfolio Adviser, and View Only dashboards',
    ],
  },
  {
    title: 'Platform extensibility',
    items: [
      'Client extract import with checks before anything is saved',
      'Per-client export and erasure for your data-protection duties',
      'Same methodology and evidence rules on every plan',
      'Integration with your systems scoped as an Enterprise project',
    ],
  },
];

const roleCards = [
  {
    title: 'Admin',
    description:
      'Controls setup, client data, team provisioning, reporting and the subscription. On Solo the admin is also the adviser and works the whole book.',
  },
  {
    title: 'Relationship Manager',
    description:
      'Gets prioritized outreach lists, customer insights, own-book visibility, and action-oriented dashboard guidance.',
  },
  {
    title: 'Compliance Officer',
    description:
      'Business role that monitors activity, exportable audit information, and governance-oriented score views.',
  },
  {
    title: 'Head of Advisory',
    description:
      'Reviews adoption, conversion, team coverage, and strategic advisory signals across the same client base.',
  },
  {
    title: 'Portfolio Adviser',
    description:
      'Reviews portfolio conversations, benchmark context, drift, mismatch severity, and customer-level investment signals.',
  },
  {
    title: 'View Only',
    description:
      'Gives stakeholders controlled read-only access to approved dashboards and reporting views.',
  },
];

const PlatformPlansPage = () => {
  useDocumentMeta({
    title: 'FinanSee Platform plans — Solo, Team, Business, Enterprise',
    description:
      'Solo €60/month for one adviser, Team €249/month to coordinate an advisory firm, Business €490/month to oversee the whole client book, Enterprise scoped to your systems. Prices exclude VAT; 30-day trial without card.',
    canonical: 'https://playground.finansee.eu/platform',
  });

  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const purchaseIntent = useMemo(
    () => normalizePurchaseIntent(location.search),
    [location.search]
  );
  const registerHref = buildPurchaseIntentUrl('/register', purchaseIntent);
  const loginHref = buildPurchaseIntentUrl('/login', purchaseIntent);

  useEffect(() => {
    if (token) {
      navigate(
        purchaseIntent.plan
          ? buildPurchaseIntentUrl('/subscription', purchaseIntent)
          : '/dashboard'
      );
    }
  }, [token, navigate, purchaseIntent]);

  return (
    <Box minH="100vh" display="flex" flexDirection="column" bg="brand.quantumBg">
      <Navbar />

      <Box className="grid-bg-dark quantum-mesh" flex="1" overflow="hidden">
        <Container maxW="container.xl" position="relative" zIndex={1} py={{ base: 10, md: 16 }}>
          <VStack spacing={{ base: 10, md: 14 }} align="stretch">
            <Box
              id="demo"
              bg="rgba(17, 27, 46, 0.86)"
              borderRadius="3xl"
              borderWidth="1px"
              borderColor="brand.quantumBorder"
              boxShadow="0 24px 80px rgba(0, 0, 0, 0.38)"
              px={{ base: 6, md: 10 }}
              py={{ base: 8, md: 10 }}
              backdropFilter="blur(12px)"
            >
              <Stack direction={{ base: 'column', lg: 'row' }} spacing={10} justify="space-between">
                <VStack align="flex-start" spacing={4} flex="1">
                  <Badge bg="rgba(0, 212, 170, 0.1)" color="brand.quantumAccent" px={3} py={1} borderRadius="full" letterSpacing="0.15em">
                    Plans & capabilities
                  </Badge>
                  <Heading fontSize={{ base: '38px', md: '56px' }} color="white" lineHeight="1.05">
                    Solo, Team or Business: choose the work you need to do.
                  </Heading>
                  <Text fontSize="lg" color="brand.quantumMuted" maxW="3xl">
                    FinanSee helps advisers decide which clients to review and documents the evidence behind each
                    review. Solo organises one adviser’s work, Team coordinates a firm, Business controls the whole
                    book, Enterprise connects it to your systems.
                  </Text>
                  <HStack spacing={4} flexWrap="wrap">
                    <Button
                      as={Link}
                      to={registerHref}
                      size="lg"
                      bgGradient="linear(135deg, brand.quantumAccent, brand.quantumCyan)"
                      color="brand.quantumBg"
                      _hover={{ opacity: 0.9 }}
                    >
                      Start a 30-day trial
                    </Button>
                    <Button as={Link} to={loginHref} variant="outline" borderColor="whiteAlpha.300" color="white" size="lg" _hover={{ bg: 'whiteAlpha.100' }}>
                      Log in
                    </Button>
                  </HStack>
                </VStack>

                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} flex="1">
                  {[
                    ['Solo · €60/month', 'One adviser: your review list, evidence, actions, outcomes and reports.'],
                    ['Team · €249/month', 'Assign clients, each adviser works their book, the lead follows the work.'],
                    ['Business · €490/month', 'Book-wide cases with EUR amounts, specialist roles and the audit console.'],
                    ['Enterprise · scoped', 'Business inside your systems: data exchange, identity, contract terms.'],
                  ].map(([title, body]) => (
                    <Box key={title} bg="brand.quantumCard" borderRadius="2xl" p={5} borderWidth="1px" borderColor="brand.quantumBorder">
                      <Text fontSize="xs" fontWeight="800" color="brand.quantumAccent" textTransform="uppercase" letterSpacing="0.12em" mb={2}>
                        {title}
                      </Text>
                      <Text color="brand.quantumMuted">
                        {body}
                      </Text>
                    </Box>
                  ))}
                </SimpleGrid>
              </Stack>
            </Box>

            <Box>
              <Heading size="xl" mb={3} color="white">
                What the platform includes
              </Heading>
              <Text color="brand.quantumMuted" fontSize="lg" mb={8} maxW="4xl">
                The B2B platform is designed to move from onboarding and analysis to action,
                reporting, and enterprise rollout without forcing each team into the same view.
              </Text>

              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
                {capabilityColumns.map((column) => (
                  <Box key={column.title} bg="brand.quantumCard" borderRadius="2xl" p={6} borderWidth="1px" borderColor="brand.quantumBorder" boxShadow="0 18px 48px rgba(0, 0, 0, 0.28)">
                    <Heading size="md" color="white" mb={4}>
                      {column.title}
                    </Heading>
                    <List spacing={3}>
                      {column.items.map((item) => (
                        <ListItem key={item} display="flex" alignItems="flex-start">
                          <ListIcon as={CheckIcon} color="brand.quantumAccent" mt={1} />
                          <Text color="brand.quantumMuted">{item}</Text>
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                ))}
              </SimpleGrid>
            </Box>

            <Box>
              <Heading size="xl" mb={3} color="white">
                Role-based product experience
              </Heading>
              <Text color="brand.quantumMuted" fontSize="lg" mb={8} maxW="4xl">
                FinanSee is not a single dashboard for everyone. Different teams access the same
                data foundation through role-appropriate views and actions.
              </Text>

              <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={6}>
                {roleCards.map((card) => (
                  <Box key={card.title} bg="brand.quantumCard" borderRadius="2xl" p={6} borderWidth="1px" borderColor="brand.quantumBorder" boxShadow="0 18px 48px rgba(0, 0, 0, 0.28)">
                    <Heading size="md" mb={3} color="white">
                      {card.title}
                    </Heading>
                    <Text color="brand.quantumMuted">{card.description}</Text>
                  </Box>
                ))}
              </SimpleGrid>
            </Box>

            <Box bg="rgba(0, 212, 170, 0.08)" borderRadius="2xl" p={{ base: 5, md: 7 }} borderWidth="1px" borderColor="rgba(34, 211, 238, 0.28)">
              <Badge bg="rgba(0, 212, 170, 0.12)" color="brand.quantumAccent" mb={3}>
                Business Audits & Compliance
              </Badge>
              <Heading size="xl" mb={3} color="white">
                Governance controls belong in the Business tenant.
              </Heading>
              <Text color="brand.quantumMuted" fontSize="lg" mb={6} maxW="4xl">
                Business adds the audit console and specialist compliance access needed to review user activity,
                generate compliance reports, and separate oversight from day-to-day RM workflows.
              </Text>
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                {[
                  'Audit logs and user activity review',
                  'Compliance role dashboards and score inspection',
                  'Exportable compliance reporting for internal review',
                ].map((item) => (
                  <Box key={item} bg="rgba(17, 27, 46, 0.78)" borderRadius="xl" p={4} borderWidth="1px" borderColor="brand.quantumBorder">
                    <Text color="white" fontWeight="800">{item}</Text>
                  </Box>
                ))}
              </SimpleGrid>
            </Box>

            <Box>
              <Heading size="xl" mb={3} color="white">
                Compare plan coverage
              </Heading>
              <Text color="brand.quantumMuted" fontSize="lg" mb={6} maxW="4xl">
                Each step adds a different kind of work: individual review, coordination, control, integration.
              </Text>
              <PlanComparisonTable variant="dark" />
            </Box>

            <Box>
              <HStack justify="space-between" align={{ base: 'flex-start', md: 'center' }} flexDirection={{ base: 'column', md: 'row' }} spacing={4} mb={8}>
                <Box>
                  <Heading size="xl" mb={3} color="white">
                    Plans and commercial paths
                  </Heading>
                  <Text color="brand.quantumMuted" fontSize="lg" maxW="4xl">
                    Prices exclude VAT. You can start directly on the plan that matches your work; there is no
                    required sequence.
                  </Text>
                </Box>
              </HStack>

              <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} spacing={6}>
                {PLAN_ORDER.map((key) => (
                  <PlanCard
                    key={key}
                    planKey={key}
                    variant="dark"
                    highlighted={purchaseIntent.plan === key}
                    badge={purchaseIntent.plan === key ? 'Your selected plan' : null}
                    actions={key === 'enterprise' ? (
                      <Button as="a" href="mailto:info@finansee.eu?subject=FinanSee%20Enterprise%20scoping" variant="outline" borderColor="whiteAlpha.300" color="white" _hover={{ bg: 'whiteAlpha.100' }}>
                        Scope a project
                      </Button>
                    ) : (
                      <Button
                        as={Link}
                        to={buildPurchaseIntentUrl('/register', { ...purchaseIntent, plan: key })}
                        bgGradient="linear(135deg, brand.quantumAccent, brand.quantumCyan)"
                        color="brand.quantumBg"
                        _hover={{ opacity: 0.9 }}
                      >
                        {OFFER.trial.selfServePlans.includes(key) ? 'Start a 30-day trial' : `Choose ${OFFER.plans[key].name}`}
                      </Button>
                    )}
                  />
                ))}
              </SimpleGrid>
              <Text color="brand.quantumSubtle" fontSize="sm" mt={6} maxW="5xl">
                {DATA_NEEDED} Trial: {OFFER.trial.days} days, no card, no automatic conversion; Solo and Team start
                on their own, a Business trial is opened on request for a case with suitable data. Optional services:
                assisted onboarding (Team {formatEuro(OFFER.services.assistedOnboardingTeam.price)}, Business {formatEuro(OFFER.services.assistedOnboardingBusiness.price)}),
                diagnostic Flash {formatEuro(OFFER.services.flashDiagnostic.price)} when you want us to analyse a sample of your data first,
                additional agreed work {formatEuro(OFFER.services.additionalWorkHourly.price)}/hour. None is required to subscribe.
              </Text>
            </Box>

            <Box bg="brand.quantumCard" color="white" borderRadius="2xl" p={6} borderWidth="1px" borderColor="brand.quantumBorder" boxShadow="0 18px 48px rgba(0, 0, 0, 0.28)">
              <Stack direction={{ base: 'column', md: 'row' }} align={{ base: 'stretch', md: 'center' }} justify="space-between" spacing={5}>
                <Box>
                  <Heading size="md" mb={3}>
                    Ready to explore the product?
                  </Heading>
                  <Text color="brand.quantumMuted" maxW="3xl">
                    Start a 30-day Solo or Team trial on your own data, or sign in if your firm already has an account.
                  </Text>
                </Box>
                <HStack spacing={3} flexWrap="wrap">
                  <Button as={Link} to={registerHref} bgGradient="linear(135deg, brand.quantumAccent, brand.quantumCyan)" color="brand.quantumBg" _hover={{ opacity: 0.9 }}>
                    Start a 30-day trial
                  </Button>
                  <Button as={Link} to={loginHref} variant="outline" borderColor="whiteAlpha.300" color="white" _hover={{ bg: 'whiteAlpha.100' }}>
                    Log in
                  </Button>
                </HStack>
              </Stack>
            </Box>
          </VStack>
        </Container>
      </Box>

      <Footer />
    </Box>
  );
};

export default PlatformPlansPage;
